/**
 * QueryPanel — phase-2 query resolution for the two-phase coding bundle.
 *
 * Renders one card per open CodingQuery (yes/no and multiple-choice as radio
 * groups with an "Other — please specify" free-text reveal; open-ended as a
 * textarea), an answered-N-of-M finalize bar, and a cascade-round banner when
 * a resolve returns new queries (iteration > 1).
 *
 * Reference UX: copilot's EHR Sim query cards (cdi-ui.js renderQueryCard +
 * ehr-bundle-ui.js renderPxQueryCard), re-implemented in React.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { CodingQuery, QueryAnswerInput } from '../services/api';

const OTHER_OPTION_ID = '__other__';

interface QueryPanelProps {
  queries: CodingQuery[];
  iteration?: number;
  busy?: boolean;
  /** Called with the assembled answers when the clinician clicks Finalize. */
  onSubmit: (answers: QueryAnswerInput[]) => void;
  errorText?: string | null;
}

interface DraftAnswer {
  optionId?: string;     // selected option_id, or OTHER_OPTION_ID
  freeText?: string;     // free text for open_ended or "Other"
}

export function QueryPanel({ queries, iteration = 1, busy = false, onSubmit, errorText }: QueryPanelProps) {
  const [drafts, setDrafts] = useState<Record<string, DraftAnswer>>({});

  const openQueries = useMemo(
    () => (queries || []).filter((q) => (q.status || 'open') === 'open'),
    [queries],
  );

  const isAnswered = useCallback((q: CodingQuery): boolean => {
    const d = drafts[q.query_id];
    if (!d) return false;
    if ((q.format || 'multiple_choice') === 'open_ended') {
      return !!(d.freeText && d.freeText.trim());
    }
    if (d.optionId === OTHER_OPTION_ID) return !!(d.freeText && d.freeText.trim());
    return !!d.optionId;
  }, [drafts]);

  const answeredCount = openQueries.filter(isAnswered).length;
  const allAnswered = openQueries.length > 0 && answeredCount === openQueries.length;

  const setOption = useCallback((queryId: string, optionId: string) => {
    setDrafts((prev) => ({ ...prev, [queryId]: { ...prev[queryId], optionId } }));
  }, []);

  const setFreeText = useCallback((queryId: string, freeText: string) => {
    setDrafts((prev) => ({ ...prev, [queryId]: { ...prev[queryId], freeText } }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (busy || !allAnswered) return;
    const answers: QueryAnswerInput[] = openQueries.map((q) => {
      const d = drafts[q.query_id] || {};
      if ((q.format || 'multiple_choice') === 'open_ended' || d.optionId === OTHER_OPTION_ID) {
        return { query_id: q.query_id, free_text: (d.freeText || '').trim() };
      }
      return { query_id: q.query_id, option_id: d.optionId };
    });
    onSubmit(answers);
  }, [busy, allAnswered, openQueries, drafts, onSubmit]);

  if (openQueries.length === 0) return null;

  return (
    <div style={wrapStyle}>
      <div style={panelHeaderStyle}>
        <span style={{ fontWeight: 700 }}>Clarifications needed</span>
        <span style={counterStyle}>{answeredCount} / {openQueries.length} answered</span>
      </div>

      {iteration > 1 && (
        <div style={roundBannerStyle}>
          Round {iteration} — your answers raised new clarifications. Please review the questions below.
        </div>
      )}

      {errorText && <div style={errorBannerStyle}>{errorText}</div>}

      {openQueries.map((q) => (
        <QueryCard
          key={q.query_id}
          query={q}
          draft={drafts[q.query_id]}
          answered={isAnswered(q)}
          disabled={busy}
          onSelect={(optId) => setOption(q.query_id, optId)}
          onFreeText={(text) => setFreeText(q.query_id, text)}
        />
      ))}

      <div style={finalizeBarStyle}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {allAnswered
            ? 'All clarifications answered — finalize to update the codes.'
            : 'Answer every question to enable Finalize.'}
        </span>
        <button
          onClick={handleSubmit}
          disabled={busy || !allAnswered}
          style={finalizeBtnStyle(busy || !allAnswered)}
        >
          {busy ? 'Finalizing…' : 'Finalize answers'}
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Single query card
// ─────────────────────────────────────────────────────────────────────────────

interface QueryCardProps {
  query: CodingQuery;
  draft?: DraftAnswer;
  answered: boolean;
  disabled: boolean;
  onSelect: (optionId: string) => void;
  onFreeText: (text: string) => void;
}

function QueryCard({ query, draft, answered, disabled, onSelect, onFreeText }: QueryCardProps) {
  const format = query.format || 'multiple_choice';
  const showOtherText = draft?.optionId === OTHER_OPTION_ID;

  // "Other, please specify" arrives as a regular option from the engines —
  // detect it so its radio reveals the free-text box instead of duplicating it.
  const isOtherLabel = (label: string) =>
    /^other\b/i.test(label.trim()) && /specify/i.test(label);

  return (
    <div style={cardStyle(answered)}>
      <div style={cardTopStyle}>
        {(query.domain || []).map((d) => (
          <span key={d} style={domainTagStyle(d)}>{d.toUpperCase()}</span>
        ))}
        {query.line_ref && <span style={lineRefStyle}>line {query.line_ref}</span>}
        {query.scope === 'encounter' && <span style={lineRefStyle}>encounter</span>}
        {answered && <span style={answeredTickStyle}>✓ answered</span>}
      </div>

      <div style={questionStyle}>{query.question}</div>
      {query.rationale && <div style={rationaleStyle}>{query.rationale}</div>}

      {format === 'open_ended' ? (
        <textarea
          style={textareaStyle}
          rows={3}
          placeholder="Type your answer…"
          value={draft?.freeText || ''}
          disabled={disabled}
          onChange={(e) => onFreeText(e.target.value)}
        />
      ) : (
        <div role="radiogroup" style={{ marginTop: 8 }}>
          {(query.options || []).map((opt) => {
            const otherOpt = isOtherLabel(opt.label || '');
            const value = otherOpt ? OTHER_OPTION_ID : opt.option_id;
            const checked = draft?.optionId === value;
            return (
              <label key={opt.option_id} style={optionLabelStyle(checked)}>
                <input
                  type="radio"
                  name={query.query_id}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onSelect(value)}
                  style={{ marginRight: 8, flexShrink: 0 }}
                />
                <span>
                  {opt.label}
                  {opt.consequence_summary && (
                    <span style={consequenceStyle}> — {opt.consequence_summary}</span>
                  )}
                </span>
              </label>
            );
          })}
          {showOtherText && (
            <textarea
              style={{ ...textareaStyle, marginTop: 6 }}
              rows={2}
              placeholder="Please specify…"
              value={draft?.freeText || ''}
              disabled={disabled}
              onChange={(e) => onFreeText(e.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const wrapStyle: React.CSSProperties = {
  margin: '0 12px 16px',
};

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 6,
  padding: '10px 2px 8px',
  fontSize: 14,
  color: '#111827',
};

const counterStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#4338ca',
  background: '#e0e7ff',
  borderRadius: 999,
  padding: '2px 10px',
};

const roundBannerStyle: React.CSSProperties = {
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  color: '#c2410c',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 12.5,
  marginBottom: 10,
};

const errorBannerStyle: React.CSSProperties = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 12.5,
  marginBottom: 10,
};

function cardStyle(answered: boolean): React.CSSProperties {
  return {
    border: `1px solid ${answered ? '#bbf7d0' : '#e5e7eb'}`,
    background: answered ? '#f0fdf4' : '#ffffff',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 10,
  };
}

const cardTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 6,
};

function domainTagStyle(d: 'dx' | 'px'): React.CSSProperties {
  const dx = d === 'dx';
  return {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    color: dx ? '#1d4ed8' : '#6d28d9',
    background: dx ? '#dbeafe' : '#ede9fe',
    borderRadius: 4,
    padding: '1px 6px',
  };
}

const lineRefStyle: React.CSSProperties = {
  fontSize: 10.5,
  color: '#6b7280',
  background: '#f3f4f6',
  borderRadius: 4,
  padding: '1px 6px',
};

const answeredTickStyle: React.CSSProperties = {
  marginLeft: 'auto',
  fontSize: 11,
  fontWeight: 600,
  color: '#15803d',
};

const questionStyle: React.CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: '#111827',
  lineHeight: 1.4,
};

const rationaleStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 4,
  lineHeight: 1.45,
};

function optionLabelStyle(checked: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: 12.5,
    color: '#1f2937',
    padding: '6px 8px',
    borderRadius: 8,
    border: `1px solid ${checked ? '#c7d2fe' : 'transparent'}`,
    background: checked ? '#eef2ff' : 'transparent',
    cursor: 'pointer',
    marginBottom: 2,
  };
}

const consequenceStyle: React.CSSProperties = {
  color: '#9ca3af',
  fontStyle: 'italic',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 12.5,
  fontFamily: 'inherit',
  resize: 'vertical',
  marginTop: 8,
};

const finalizeBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 12,
  padding: '10px 2px',
  borderTop: '1px solid #f3f4f6',
  marginTop: 4,
};

function finalizeBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    border: 'none',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    color: '#ffffff',
    background: disabled ? '#a5b4fc' : '#4f46e5',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
